// Comparison tables for article content, which is a list of HTML strings
// (KnowledgeArticle.tsx renders them as-is). Building the markup here gives
// every table the same accessible structure: a visible caption, column and
// row headers with scope, and a scroll container that is labelled by the
// caption and takes keyboard focus, so a table wider than a phone scrolls
// on its own without the page scrolling sideways.

export interface ComparisonRow {
  label: string;
  cells: string[];
}

export interface ComparisonTable {
  id: string; // unique on the page; the caption's id is derived from it
  caption: string;
  rowHeader: string; // names the row-label column for screen readers
  columns: string[]; // one per compared item
  rows: ComparisonRow[];
}

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const headerRow = (rowHeader: string, columns: string[]): string =>
  `<tr><th scope="col"><span class="sr-only">${escapeHtml(rowHeader)}</span></th>${columns
    .map((column) => `<th scope="col">${escapeHtml(column)}</th>`)
    .join('')}</tr>`;

const bodyRow = ({ label, cells }: ComparisonRow, columnCount: number): string => {
  if (cells.length !== columnCount) {
    throw new Error(`Comparison row "${label}" has ${cells.length} cells for ${columnCount} columns`);
  }
  return `<tr><th scope="row">${escapeHtml(label)}</th>${cells
    .map((cell) => `<td>${escapeHtml(cell)}</td>`)
    .join('')}</tr>`;
};

export function comparisonTable({ id, caption, rowHeader, columns, rows }: ComparisonTable): string {
  const captionId = `${id}-caption`;
  return [
    `<div class="table-scroll" role="region" aria-labelledby="${captionId}" tabindex="0">`,
    // The span lets the caption wrap to the visible width on phones (index.css).
    `<table><caption id="${captionId}"><span>${escapeHtml(caption)}</span></caption>`,
    `<thead>${headerRow(rowHeader, columns)}</thead>`,
    `<tbody>${rows.map((row) => bodyRow(row, columns.length)).join('')}</tbody>`,
    '</table></div>',
  ].join('');
}
